import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Users,
  UserPlus,
  Filter,
  MoreHorizontal,
  LogOut,
  Trash2,
  KeyRound,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem, HeroMetaSep } from "@/components/brand/Hero";
import { Pill } from "@/components/brand/Pill";
import { Card, SectionHead, Mantra, MantraAccent } from "@/components/brand/Card";
import { Toolbar, ToolbarChip, ToolbarSearch, ToolbarSpacer } from "@/components/brand/Toolbar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAdminUsers,
  useCreateAdminUser,
  useInviteFromDirectory,
  useUpdateAdminUser,
  useDeleteAdminUser,
  useKickAdminUser,
  useWorkspaceSettings,
} from "@/hooks/useApi";
import { useCurrentUser } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  hasMinRole,
  isManagerAssignable,
  MANAGER_ASSIGNABLE_ROLES,
  ROLES,
  type Role,
  roleBlurb,
  roleLabel,
  roleTone,
} from "@/lib/roles";
import { formatRelative, formatDateTime } from "@/lib/datetime";
import { TOUCH_TARGET_CLASS } from "@/lib/a11y";
import { cn } from "@/lib/utils";
import { errorDetailOr } from "@/lib/api-error";
import { avatarColor, initialsOf } from "@/lib/avatar";
import type { AdminUserItem } from "@/api/admin-users";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

/**
 * Em que pé está o acesso desta pessoa.
 *
 * Isto já foi `accessDoor`, e respondia "por qual porta ela entra": senha,
 * Microsoft, as duas, ou nenhuma. Eram quatro estados porque havia duas portas,
 * e o par (`password_login_enabled`, `is_entra_linked`) era a única leitura
 * honesta — `auth_provider` sozinho mentia, porque vincular ao diretório por
 * e-mail deliberadamente não o reescrevia.
 *
 * Com o Entra como única porta, a pergunta muda de "por onde" para "já entrou",
 * e sobram três estados. Um deles é novo e vale a coluna existir sozinho:
 *
 * | estado | quando | o que significa |
 * |---|---|---|
 * | `active` | `is_entra_linked` | já entrou pelo diretório ao menos uma vez |
 * | `pending` | pré-registrada, sem `entra_oid` | tem papel, ainda não apareceu |
 * | `none` | `sso_enabled = FALSE` no workspace | **ninguém** entra, esta incluída |
 *
 * ⚠️ O `none` agora é do **workspace inteiro**, não de uma linha. Antes ele era
 * a conta que ficou sem porta quando o admin desligava o SSO — as outras
 * seguiam entrando por senha. Sem senha, desligar o SSO tranca todo mundo, e é
 * essa a razão de o portão do workspace continuar entrando na conta: uma leitura
 * que olhasse só a linha diria "ativa" para gente que não consegue entrar.
 */
type AccessState = "active" | "pending" | "none";

function accessState(user: AdminUserItem, ssoEnabled: boolean): AccessState {
  if (!ssoEnabled) return "none";
  return user.is_entra_linked ? "active" : "pending";
}

function accessLabel(state: AccessState, t: TFunction): string {
  switch (state) {
    case "active":
      return t("Microsoft");
    case "pending":
      return t("Awaiting first sign-in");
    default:
      return t("No way in");
  }
}



// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type RoleFilter = Role | "all";

export default function AdminTeam() {
  const { t } = useTranslation();
  const { data, isLoading } = useAdminUsers();
  const { data: workspace } = useWorkspaceSettings();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [activeOnly, setActiveOnly] = useState(false);

  // `GET /api/admin/workspace` exige só `roles.USER`, então um admin lê sem
  // problema. Sem SSO no tenant, a API recusa papel acima de `user` em conta que
  // entra por senha — a tela precisa saber disso para não oferecer o que será negado.
  const ssoEnabled = workspace?.sso_enabled ?? false;

  const users = useMemo(() => data?.users ?? [], [data?.users]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((u) => {
      if (activeOnly && !u.is_active) return false;
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!needle) return true;
      return u.name.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle);
    });
  }, [users, search, roleFilter, activeOnly]);

  // Contas ativas que ainda não entraram uma vez pelo diretório.
  //
  // ⚠️ **Não passa pelo `accessState`, e a diferença é o ponto.** Aquele responde
  // "esta pessoa entra agora?" e curto-circuita em `none` quando o workspace está
  // trancado — reusá-lo aqui zerava o KPI exatamente quando havia problema, com
  // gente genuinamente esperando o primeiro acesso. Este número responde outra
  // pergunta, e ela é sobre a linha: quem está pré-registrado e ainda não
  // apareceu. O portão do workspace não entra nela, e é justamente com o portão
  // fechado que o admin precisa deste número para saber o que acontece depois de
  // religar.
  const pendingCount = users.filter((u) => u.is_active && !u.is_entra_linked).length;

  // O workspace inteiro trancado. Vale um aviso próprio porque não é estado de
  // conta nenhuma: é o toggle de SSO desligado em /admin/settings.
  const workspaceLocked = !ssoEnabled && users.some((u) => u.is_active);

  const kpis = [
    {
      label: t("Members"),
      value: (data?.total_count ?? 0).toLocaleString(),
      sub: data?.active_count != null ? `${data.active_count} ${t("active")}` : "—",
    },
    {
      label: t("Administrators"),
      value: (data?.admin_count ?? 0).toLocaleString(),
      sub: t("Reach every project"),
    },
    {
      label: t("Awaiting first sign-in"),
      value: pendingCount.toLocaleString(),
      sub: t("Pre-registered, never signed in"),
    },
    {
      label: t("Inactive"),
      value: ((data?.total_count ?? 0) - (data?.active_count ?? 0)).toLocaleString(),
      sub: t("Soft-deleted"),
    },
  ];

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<Users />}
        overline={t("ADMIN · PEOPLE & ACCESS")}
        title={t("Team")}
        meta={
          <>
            <HeroMetaItem k={t("Members")} v={`${data?.active_count ?? 0} ${t("active")}`} />
            <HeroMetaSep />
            {/* Reflete o estado real do workspace, em vez de prometer "coming soon"
                para algo que já existe desde a S-062. */}
            <HeroMetaItem k="SSO" v={ssoEnabled ? t("On") : t("Off")} />
          </>
        }
        actions={
          <Button
            variant="action"
            size="sm"
            className="h-9 gap-2 px-3.5 text-xs font-semibold"
            onClick={() => setInviteOpen(true)}
          >
            <UserPlus className="w-3.5 h-3.5" /> {t("Invite people")}
          </Button>
        }
        kpis={kpis}
      />

      <div className="p-8 space-y-7">
        {workspaceLocked && (
          <div className="flex items-start gap-2.5 rounded-md border border-[rgba(212,53,60,0.2)] bg-[rgba(212,53,60,0.06)] px-4 py-3 text-sm text-[#08254c]">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#d4353c]" />
            <span>
              {/* As duas metades são verdade em momentos diferentes, e o banner
                  precisa das duas. Quem o lê tem sessão viva por definição — a
                  rota passa pelo `AuthGuard` —, então para um admin a ação em
                  Settings ainda existe, e é a mensagem que importa: corre. Mas
                  ela acaba quando a última sessão acabar, e aí o conserto é o
                  banco. Dizer só a primeira metade contradizia o diálogo de
                  `/admin/settings`, que diz só a segunda. */}
              {t(
                "Microsoft sign-in is off for this workspace, and it is the only way in. Nobody can sign in — including you, once this session expires. You can still turn it back on in Settings while this session lasts; after that, recovery means going into the database.",
              )}
            </span>
          </div>
        )}

        <section>
          <SectionHead
            overline={t("THE ROSTER")}
            title={t("Members")}
            tag={`${shown.length} ${t(shown.length === 1 ? "person" : "people")}`}
            sub={t("Change the role inline. Use the row menu to kick sessions or deactivate.")}
          />
          <Card>
            <Toolbar>
              <ToolbarSearch
                placeholder={t("Search members...")}
                value={search}
                onChange={setSearch}
              />
              <ToolbarChip active={roleFilter === "all"} onClick={() => setRoleFilter("all")}>
                {t("All")}
              </ToolbarChip>
              {/* Do maior alcance para o menor: quem abre a tela para auditar
                  procura admin primeiro. */}
              {[...ROLES].reverse().map((r) => (
                <ToolbarChip key={r} active={roleFilter === r} onClick={() => setRoleFilter(r)}>
                  {roleLabel(r, t)}
                </ToolbarChip>
              ))}
              <ToolbarSpacer />
              <ToolbarChip
                icon={<Filter />}
                active={activeOnly}
                onClick={() => setActiveOnly((v) => !v)}
              >
                {t("Active only")}
              </ToolbarChip>
            </Toolbar>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: "32%" }}>{t("Members")}</th>
                  <th style={{ width: 190 }}>{t("Role")}</th>
                  <th>{t("Way in")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Last active")}</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
                ) : shown.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-[#7b8799] text-sm">
                      {users.length === 0 ? (
                        <>
                          {t("No team members yet — click")} <strong>{t("Invite people")}</strong>{" "}
                          {t("to add the first one.")}
                        </>
                      ) : (
                        t("No member matches this filter.")
                      )}
                    </td>
                  </tr>
                ) : (
                  shown.map((u) => (
                    <MemberRow
                      key={u.id}
                      user={u}
                      ssoEnabled={ssoEnabled}
                      adminCount={data?.admin_count ?? 0}
                    />
                  ))
                )}
              </tbody>
            </table>
            <div className="px-4 py-3 flex justify-between items-center border-t border-[#f2f4f7] font-mono text-[11px] text-[#7b8799]">
              <span>
                {t("Showing {{shown}} of {{total}}", {
                  shown: shown.length.toLocaleString(),
                  total: (data?.total_count ?? 0).toLocaleString(),
                })}
              </span>
            </div>
          </Card>
        </section>

        {/* O card "Recent access events" volta quando houver a trilha
            `mygrow.auth_event`, adiada na S-073 (decisão 11). Até lá, "quem
            entrou, quando" é a coluna "Last active" e o log do servidor (#1042). */}

        <Mantra>
          {t("ONE ROSTER")} · <MantraAccent>{t("CLEAR RESPONSIBILITIES")}</MantraAccent> ·{" "}
          {t("INSTANT REVOCATION")}
        </Mantra>
      </div>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} ssoEnabled={ssoEnabled} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row actions
// ---------------------------------------------------------------------------

/** Confirmações pendentes da linha. `null` = nenhum diálogo aberto. */
type PendingAction =
  | { kind: "kick" }
  | { kind: "deactivate" }
  | { kind: "remove" }
  | null;

/**
 * Título do diálogo de confirmação. Fora do JSX: os ramos aninhados ali viram sopa.
 *
 * Eram seis. Três saíram com o login por senha: `promote` (a conversão que
 * promover uma conta com senha exigia), `revokePassword` e `restorePassword`.
 *
 * ⚠️ **`deactivate` tem `case` próprio, e o `default` não devolve texto.** As três
 * funções de texto eram `switch (pending?.kind)` com `deactivate` morando no
 * `default` — que também é onde cai `pending === null`. Um render sem ação
 * descrevia, com todas as letras, a ação de desativar: quem confirmava "Remover
 * Fulano?" via o diálogo sair de cena dizendo "Desativar Fulano?".
 *
 * Quem conserta isso de verdade é o `describedAction` do `MemberRow`, que mantém
 * a ação viva durante o fechamento. Isto aqui é a segunda guarda, e existe porque
 * ela é a única **por construção**: sem ação, não há afirmação nenhuma — string
 * vazia. Um diálogo em branco é falha óbvia; um diálogo citando outra ação
 * destrutiva é falha que engana.
 */
function dialogTitle(pending: PendingAction, user: AdminUserItem, t: TFunction): string {
  switch (pending?.kind) {
    case "kick":
      return t("End every session of {{name}}?", { name: user.name });
    case "remove":
      return t("Remove {{name}}?", { name: user.name });
    case "deactivate":
      return user.is_active
        ? t("Deactivate {{name}}?", { name: user.name })
        : t("Reactivate {{name}}?", { name: user.name });
    default:
      return "";
  }
}

/** O que acontece de verdade. É a frase que evita o clique arrependido. */
function dialogBody(pending: PendingAction, user: AdminUserItem, t: TFunction): string {
  switch (pending?.kind) {
    case "kick":
      return t("They stay active and can sign in again right away.");
    /**
     * Desativar e Remover levam a conta ao mesmo lugar visível — `is_active =
     * FALSE`, linha apagada no roster —, e por isso diziam a **mesma frase**. O
     * admin escolhia entre duas ações sem informação para escolher.
     *
     * A diferença não é "perder o acesso": os dois cortam na hora, porque a
     * consulta de sessão filtra `is_active = TRUE`. Ela está no
     * `user_generation`, que só o `DELETE` incrementa — e só aparece **na
     * volta**. Reativar um desativado revalida os tokens que já existiam;
     * reativar um removido não, porque a geração deles ficou para trás.
     */
    case "remove":
      return t(
        "They lose access immediately, and every token they hold dies for good. Reversible from the row menu, but they will have to sign in again.",
      );
    case "deactivate":
      return user.is_active
        ? t(
            "They lose access immediately. Reactivating restores the role they already had, and a tab left open resumes without signing in again.",
          )
        : t("They get access back with the role they already had.");
    default:
      return "";
  }
}

/**
 * O rótulo do botão que confirma. Função, e não mapa por `kind`, **por causa do
 * defeito que ela conserta**: era um `Record` indexado só pelo `kind`, enquanto
 * título e corpo eram funções que enxergavam a conta. "Desativar" e "Reativar"
 * são o mesmo `kind` — o payload é `is_active: !user.is_active` —, então o
 * diálogo de reativar abria com o botão escrito "Desativar".
 *
 * Ficar na mesma forma que `dialogTitle` e `dialogBody` é o que impede a
 * divergência voltar: as três peças recebem os mesmos argumentos, e qualquer
 * ramo que uma precise está disponível às outras duas.
 */
function confirmLabel(pending: PendingAction, user: AdminUserItem, t: TFunction): string {
  switch (pending?.kind) {
    case "kick":
      return t("End sessions");
    case "remove":
      return t("Remove");
    case "deactivate":
      return user.is_active ? t("Deactivate") : t("Reactivate");
    default:
      return "";
  }
}

function MemberRow({
  user,
  ssoEnabled,
  adminCount,
}: {
  user: AdminUserItem;
  ssoEnabled: boolean;
  /** Admins **ativos** do tenant. `admin_count` já vem assim do backend. */
  adminCount: number;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const me = useCurrentUser();
  const updateMutation = useUpdateAdminUser();
  const deleteMutation = useDeleteAdminUser();
  const kickMutation = useKickAdminUser();
  const [pending, setPending] = useState<PendingAction>(null);

  /**
   * A ação que o diálogo **está descrevendo**, que não é a mesma coisa que a ação
   * pendente.
   *
   * `pending` controla o `open`, então ele volta a `null` no instante em que o
   * diálogo começa a fechar — o `AlertDialogAction` do Radix **é** o
   * `Dialog.Close`, e dispara o `onOpenChange(false)` sozinho no clique. Só que o
   * `Presence` mantém o conteúdo montado e renderizando durante os 200ms do
   * `data-[state=closed]:animate-out`, e nesse render `pending?.kind` já é
   * `undefined`: cai no `default` das três funções de texto, que serve `deactivate`
   * e `null` ao mesmo tempo. Quem confirmava "Remover Fulano?" via o diálogo sair
   * de cena escrevendo "Desativar Fulano?", com corpo e botão no mesmo ramo —
   * citando outra ação destrutiva logo depois de uma ação destrutiva.
   *
   * Este estado **não** é limpo no fechamento: ele guarda a última ação aberta, e
   * é dele que os textos leem. Assim a saída do diálogo continua dizendo o que a
   * pessoa confirmou, sem depender de tempo, de animação ou de evento de
   * fechamento — que era a alternativa, e é a frágil: sem animação (aba em
   * background, `prefers-reduced-motion`, testes) o evento nunca chega e a ação
   * pendente ficaria presa.
   */
  const [describedAction, setDescribedAction] = useState<PendingAction>(null);

  /** Abre a confirmação. Os dois estados andam juntos na abertura, nunca no fim. */
  const askConfirmation = (action: NonNullable<PendingAction>) => {
    setDescribedAction(action);
    setPending(action);
  };

  const isSelf = me?.id === user.id;
  const state = accessState(user, ssoEnabled);

  /**
   * Ler o roster é do `manager`; mexer nele continua sendo do `admin`.
   *
   * Sem isto, abrir a listagem para o manager só mudaria o lugar do defeito: ele
   * veria o select de papel e o menu de ações inteiro, e **toda** opção ali
   * responde 403 — criar, trocar papel, derrubar sessão, desativar, remover.
   */
  const canManage = hasMinRole(me?.role, "admin");

  /**
   * Esta linha é a última vaga de admin ativo do tenant.
   *
   * Desativar ou remover quem está aqui deixa o tenant com zero admins, e não há
   * volta pela aplicação: promover e criar exigem admin, e o auto-bootstrap do JIT
   * só concede `admin` com a tabela vazia. O conserto era `UPDATE mygrow.users` no
   * banco.
   *
   * O backend agora recusa os três caminhos com 409 (S-075 Fase 2). A tela repete a
   * regra em vez de só repassar o erro: oferecer a ação, abrir a confirmação
   * destrutiva e falhar depois é pior que não oferecer.
   *
   * Vale para qualquer linha, não só a própria: o furo também deixava um admin
   * desativar o outro e sobrar ninguém.
   */
  const isLastActiveAdmin = user.role === "admin" && user.is_active && adminCount <= 1;

  /** Motivo renderizado sob a ação bloqueada, ou `null`. */
  const blockedReason = isLastActiveAdmin
    ? t("The tenant needs at least one active administrator. Promote someone else first.")
    : null;

  /**
   * Aqui moravam as guardas do interruptor de senha, espelhadas do backend para a
   * tela não oferecer o que ele negaria: revogar exigia SSO no workspace, revogar
   * a própria exigia crachá vinculado, restaurar exigia que houvesse senha, e
   * restaurar só valia para `user`. As quatro saíram com a coluna
   * `password_login_enabled`, junto do aviso de conversão que promover uma conta
   * com senha exigia.
   */

  const failed = (err: unknown, fallback: string) =>
    toast({
      variant: "destructive",
      title: t("The change was not applied"),
      description: errorDetailOr(err, fallback),
    });

  // Troca direta. Já passou por confirmação, quando promover uma conta com senha
  // exigia convertê-la para SSO na mesma requisição; sem senha, não há estado
  // proibido que a troca de papel possa produzir.
  const onRoleChange = (value: string) => {
    const role = value as Role;
    if (role === user.role) return;
    updateMutation.mutate(
      { userId: user.id, payload: { role } },
      { onError: (err) => failed(err, t("Could not change the role.")) },
    );
  };

  const runPending = () => {
    if (!pending) return;
    switch (pending.kind) {
      case "kick":
        kickMutation.mutate(user.id, {
          onError: (err) => failed(err, t("Could not end the sessions.")),
        });
        break;
      case "deactivate":
        updateMutation.mutate(
          { userId: user.id, payload: { is_active: !user.is_active } },
          { onError: (err) => failed(err, t("Could not change the status.")) },
        );
        break;
      case "remove":
        deleteMutation.mutate(user.id, {
          onError: (err) => failed(err, t("Could not remove the member.")),
        });
        break;
    }
    // Nada de `setPending(null)` aqui: quem fecha é o próprio botão. O
    // `AlertDialogAction` **é** o `Dialog.Close` do Radix, então o clique já
    // dispara o `onOpenChange(false)` da linha do `<AlertDialog>`, que zera o
    // `pending`. A linha que havia era redundante — e foi por parecer a causa do
    // texto trocado na saída que ela apareceu na revisão. A causa é o fechamento
    // em si, e quem resolve é o `describedAction`.
  };

  return (
    <tr className={user.is_active ? "" : "opacity-60"}>
      <td>
        <div className="flex gap-3 items-center">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center font-brand font-bold text-xs shrink-0 text-white"
            style={{ background: avatarColor(user.id) }}
          >
            {initialsOf(user.name)}
          </div>
          <div>
            <div className="name flex items-center gap-1.5">
              {user.name}
              {isSelf && <span className="text-[10px] text-[#7b8799]">({t("you")})</span>}
            </div>
            <div className="mono muted">{user.email}</div>
          </div>
        </div>
      </td>
      <td>
        {/* Dois motivos para o papel virar rótulo em vez de campo:
            — **não é admin**: `PATCH /api/admin/users/{id}` recusaria;
            — **é a própria conta**: trocar o próprio papel é o lockout que
              ninguém desfaz pela tela. O rebaixamento encerra a sessão (S-073
              decisão 4) e o menu Admin some no próximo login. Desde a S-075 Fase 2
              o backend recusa rebaixar o último admin ativo, mas a tela continua
              não oferecendo a corda: com dois admins o auto-rebaixamento é
              legítimo e passaria, e ainda assim tira o autor do próprio caminho. */}
        {!canManage || isSelf ? (
          <div className="flex items-center gap-2">
            <Pill tone={roleTone(user.role)}>{roleLabel(user.role, t)}</Pill>
            {isSelf && <span className="text-[10px] text-[#7b8799]">{t("your own role")}</span>}
          </div>
        ) : (
          <Select
            value={user.role}
            onValueChange={onRoleChange}
            disabled={updateMutation.isPending}
          >
            <SelectTrigger
              aria-label={t("Role of {{name}}", { name: user.name })}
              className="h-8 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* Os quatro, sem restrição. Os papéis acima de `user` ficavam
                  desabilitados numa conta que entrava por senha em workspace sem
                  SSO — não havia como convertê-la, e a API recusaria. Sem senha,
                  toda conta é do diretório e todo papel é concedível. */}
              {[...ROLES].reverse().map((r) => (
                <SelectItem key={r} value={r}>
                  {roleLabel(r, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </td>
      <td>
        <span className="inline-flex items-center gap-1.5">
          {state === "none" ? (
            <ShieldAlert className="w-3.5 h-3.5 text-[#d4353c]" />
          ) : state === "pending" ? (
            <KeyRound className="w-3.5 h-3.5 text-[#7b8799]" />
          ) : (
            <ShieldCheck className="w-3.5 h-3.5 text-[#27a155]" />
          )}
          <Pill tone={state === "none" ? "danger" : state === "active" ? "ok" : "muted"}>
            {accessLabel(state, t)}
          </Pill>
        </span>
      </td>
      <td>
        {user.is_active ? (
          <Pill tone="ok">{t("Active")}</Pill>
        ) : (
          <Pill tone="muted">{t("Disabled")}</Pill>
        )}
      </td>
      <td className="mono" title={formatDateTime(user.last_login_at)}>
        {formatRelative(user.last_login_at)}
      </td>
      {/* A célula fica, vazia: tirá-la desalinharia a linha do cabeçalho. */}
      {!canManage ? (
        <td />
      ) : (
        <td>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* Caixa declarada (`w-7 h-7`), não derivada de padding: `p-1`
                  sobre um ícone de 16px dava 24px, e 40px com a ampliação — 4px
                  abaixo do mínimo. Mesma medida do gatilho de `/projects`. */}
              <button
                aria-label={t("Actions for {{name}}", { name: user.name })}
                className={cn(
                  "w-7 h-7 flex items-center justify-center rounded hover:bg-[#f2f4f7] text-[#7b8799]",
                  TOUCH_TARGET_CLASS,
                )}
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {/* O interruptor de senha ficava aqui, e era o ponto inteiro da
                  S-069: ligar e desligar `password_login_enabled` por conta,
                  desabilitado com o motivo quando alguma das quatro guardas
                  barrava. Saiu com a coluna. */}
              <DropdownMenuItem
                onClick={() => askConfirmation({ kind: "kick" })}
                disabled={!user.is_active}
              >
                <LogOut className="w-3.5 h-3.5 mr-2" />
                {t("Kick session")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* `Deactivate` derruba o acesso de alguém e só um admin desfaz;
                  ficava com o mesmo peso visual de `Kick session`, que é
                  reversível no próximo login. `Reactivate` não é destrutivo. */}
              <DropdownMenuItem
                onClick={() => askConfirmation({ kind: "deactivate" })}
                disabled={isLastActiveAdmin}
                className={user.is_active ? "text-[#d4353c] focus:text-[#d4353c]" : undefined}
              >
                {user.is_active ? t("Deactivate") : t("Reactivate")}
              </DropdownMenuItem>
              {user.is_active && (
                <DropdownMenuItem
                  onClick={() => askConfirmation({ kind: "remove" })}
                  disabled={isLastActiveAdmin}
                  className="text-[#d4353c] focus:text-[#d4353c]"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-2" />
                  {t("Remove member")}
                </DropdownMenuItem>
              )}
              {/* Desabilitado **com o motivo**, não escondido: sumir com a ação faz
                  o admin achar que ela não existe e ir procurá-la no banco. */}
              {blockedReason && (
                <DropdownMenuLabel className="px-2 pb-1.5 text-[10px] leading-snug font-normal text-[#7b8799]">
                  {t("Not available:")} {blockedReason}
                </DropdownMenuLabel>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{dialogTitle(describedAction, user, t)}</AlertDialogTitle>
                <AlertDialogDescription>{dialogBody(describedAction, user, t)}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={runPending}>
                  {confirmLabel(describedAction, user, t)}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </td>
      )}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Invite dialog
// ---------------------------------------------------------------------------

/**
 * Uma forma só de criar conta: pré-registro no diretório.
 *
 * Eram duas, e elas não se misturavam — `directory` (sem senha, os quatro papéis,
 * exigindo SSO no workspace) e `password` (senha obrigatória, papel fixo em
 * `user`). O segundo modo saiu com o `temp_password`, e com ele o `<Select>` de
 * tipo de conta, o campo de senha visível e o `InviteMode`.
 *
 * O que continua valendo é a diferença entre **quem** convida: um admin usa
 * `POST /api/admin/users` e concede qualquer papel; um manager usa
 * `POST /api/auth/register` e concede no máximo `consultant`.
 */
function InviteDialog({
  open,
  onOpenChange,
  ssoEnabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ssoEnabled: boolean;
}) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  /**
   * Um manager convida, mas por outra porta e com outro teto.
   *
   * O diálogo inteiro falava com `POST /api/admin/users`, que é admin — então
   * para um manager **os dois modos davam 403**, não só o de senha. O caminho
   * dele é `POST /api/auth/register`, e ele é mais estreito de propósito:
   *
   * - **nunca aceita senha** (400 em qualquer workspace). Conta com e-mail e
   *   senha é conta de visitante, e quem define a política de autenticação do
   *   workspace é o admin, não quem convida;
   * - **concede só `consultant` ou `user`** — nem `admin`, nem outro `manager`;
   * - **exige SSO no workspace**, senão a conta nasceria sem porta de entrada.
   */
  const isAdmin = hasMinRole(me?.role, "admin");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const createAsAdmin = useCreateAdminUser();
  const inviteFromDirectory = useInviteFromDirectory();
  const mutation = isAdmin ? createAsAdmin : inviteFromDirectory;

  const assignableRoles = isAdmin ? ROLES : MANAGER_ASSIGNABLE_ROLES;

  // O SSO do workspace é pré-condição das duas rotas: sem ele a conta nasceria
  // sem porta nenhuma, e as duas respondem 400.
  const canSubmit =
    email.trim().length > 0 && name.trim().length > 0 && ssoEnabled && !mutation.isPending;

  const reset = () => {
    setEmail("");
    setName("");
    setRole("user");
    setErrorMsg(null);
  };

  /**
   * Fechar é descartar a tentativa.
   *
   * O componente não desmonta: a linha do `AdminTeam` o renderiza sempre e o
   * `open` só esconde o conteúdo do `DialogContent`. Enquanto o `reset()` estava
   * ligado só ao caminho de sucesso, cancelar preservava a tentativa inteira —
   * e-mail, nome, a senha temporária **visível** e o erro do backend —, então
   * quem reabria para convidar outra pessoa encontrava o endereço da anterior
   * mais um `400` sobre uma conta que já não estava tentando criar.
   *
   * Um lugar só para o par (limpar, fechar), usado pelo cancelar, pelo Esc, pelo
   * clique fora e pelo sucesso.
   */
  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    setErrorMsg(null);
    try {
      if (isAdmin) {
        await createAsAdmin.mutateAsync({
          email: email.trim(),
          name: name.trim(),
          role,
        });
      } else {
        await inviteFromDirectory.mutateAsync({
          email: email.trim(),
          name: name.trim(),
          // O select só oferece os dois, mas o estado é um `Role` qualquer — o
          // fallback existe para que um papel fora da lista vire o menos
          // privilegiado, nunca o mais.
          role: isManagerAssignable(role) ? role : "user",
        });
      }
      close();
    } catch (err: unknown) {
      setErrorMsg(errorDetailOr(err, t("Could not create the account.")));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t("Invite new member")}</DialogTitle>
          <DialogDescription>
            {t("They sign in with Microsoft. Pick the role they start with.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Havia um <Select> de "tipo de conta" aqui: diretório ou senha. Com
              uma forma só de criar, ele virou um campo travado num valor — e um
              campo que não escolhe nada é ruído. */}
          {!ssoEnabled && (
            <div className="rounded border border-[rgba(212,53,60,0.2)] bg-[rgba(212,53,60,0.08)] px-2.5 py-2 text-xs text-[#d4353c]">
              {t(
                "Microsoft sign-in is off for this workspace, so the account would have no way in. Turn it on in Settings first.",
              )}
            </div>
          )}
          <div>
            <Label htmlFor="invite-email">{t("Email")}</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alana.garcia@exedconsulting.com"
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="invite-name">{t("Name")}</Label>
            <Input
              id="invite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alana Garcia"
            />
          </div>
          <div>
            <Label htmlFor="invite-role">{t("Role")}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as Role)}>
              <SelectTrigger id="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assignableRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {roleLabel(r, t)} · {roleBlurb(r, t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-[#7b8799] font-mono">
            {t("No password is set. They sign in with Sign in with Microsoft on the login screen.")}
          </p>
          {errorMsg && (
            <div className="text-xs text-[#d4353c] bg-[rgba(212,53,60,0.08)] border border-[rgba(212,53,60,0.2)] rounded px-2.5 py-2">
              {errorMsg}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost-light" onClick={close}>
            {t("Cancel")}
          </Button>
          <Button variant="action" onClick={handleSubmit} disabled={!canSubmit}>
            {mutation.isPending ? t("Sending...") : t("Send invite")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SkeletonRow() {
  return (
    <tr>
      <td>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#f2f4f7]" />
          <div className="space-y-1.5">
            <div className="h-3 w-36 rounded bg-[#f2f4f7]" />
            <div className="h-2.5 w-52 rounded bg-[#f7f7f7]" />
          </div>
        </div>
      </td>
      <td>
        <div className="h-4 w-16 rounded-full bg-[#f2f4f7]" />
      </td>
      <td>
        <div className="h-4 w-20 rounded-full bg-[#f2f4f7]" />
      </td>
      <td>
        <div className="h-4 w-14 rounded-full bg-[#f2f4f7]" />
      </td>
      <td>
        <div className="h-3 w-20 rounded bg-[#f2f4f7]" />
      </td>
      <td />
    </tr>
  );
}
